import {
  EventType,
  IBrand,
  ICalendarEvent,
  ICalendarEventSchedule,
  IPractice,
  IRoster,
  IRosterTime,
  IScheduleRange,
  IStaffer,
  StafferCollection,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  CollectionReference,
  DayOfWeek,
  DAYS_OF_WEEK,
  EndingType,
  endsOnDate,
  Firestore,
  IReffable,
  isSameRef,
  ITimePeriod,
  multiSwitchMap,
  reduceToSingleArray,
  subCollection,
  Timezone,
  toISODate,
  toMomentTz,
  undeletedQuery,
  where,
  WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import * as moment from 'moment-timezone';
import { combineLatest, from, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TimezoneResolver } from '../../timezone';
import { Brand } from '../brand';
import { CalendarEventSchedule } from '../event/calendar-event-schedule';
import { OrganisationCache } from '../organisation/organisation-cache';
import { Schedule } from '../schedule/schedule';
import { scheduleTimeToTimePeriod } from '../schedule/schedule-time';

export class Roster {
  static init(): IRoster {
    const operatingDays: DayOfWeek[] = DAYS_OF_WEEK;

    const schedule: IRoster = {
      days: [],
    };

    operatingDays.map((day: DayOfWeek) => {
      schedule.days.push({
        dayOfWeek: day,
        shift: { from: '08:00', to: '18:00' },
      });
    });

    return schedule;
  }

  static col(
    staffer: IReffable<IStaffer>
  ): CollectionReference<ICalendarEventSchedule> {
    return subCollection<ICalendarEventSchedule>(
      staffer.ref,
      StafferCollection.RosterSchedules
    );
  }

  static all$(
    staffer: IReffable<IStaffer>,
    eventTypes: EventType[] = [],
    practice?: IReffable<IPractice>,
    includeRosterHistory = false
  ): Observable<WithRef<ICalendarEventSchedule>[]> {
    const collection = includeRosterHistory
      ? Roster.col(staffer)
      : undeletedQuery(Roster.col(staffer));

    return OrganisationCache.rosterSchedules
      .query$(
        {
          stafferRef: staffer.ref,
          eventTypes,
          practiceRef: practice?.ref,
          includeRosterHistory,
        },
        collection,
        ...compact([
          eventTypes.length
            ? where('item.event.type', 'in', eventTypes)
            : undefined,
        ])
      )
      .pipe(
        map((schedules) =>
          schedules.filter((schedule) =>
            practice ? isSameRef(schedule.item.event.practice, practice) : true
          )
        ),
        switchMap((schedules) =>
          includeRosterHistory
            ? from(
                asyncForEach(schedules, (schedule) =>
                  Roster.toHistoricalSchedule(schedule)
                )
              )
            : of(schedules)
        )
      );
  }

  /**
   * Ends a retired schedule when it was deleted, as an open-ended roster
   * would otherwise keep forecasting occurrences forever.
   */
  static async toHistoricalSchedule(
    schedule: WithRef<ICalendarEventSchedule>
  ): Promise<WithRef<ICalendarEventSchedule>> {
    if (!schedule.deleted) {
      return schedule;
    }

    const timezone = await TimezoneResolver.fromPracticeRef(
      schedule.item.event.practice.ref
    );

    const endingDate = toISODate(
      moment.min(
        compact([
          toMomentTz(schedule.updatedAt, timezone),
          endsOnDate(schedule.pattern)
            ? toMomentTz(schedule.pattern.endingDate, timezone)
            : undefined,
        ])
      ),
      timezone
    );

    return {
      ...schedule,
      pattern: { ...schedule.pattern, endingType: EndingType.Date, endingDate },
    };
  }

  static async hasPracticeRoster(
    staffer: IReffable<IStaffer>,
    practice: IReffable<IPractice>
  ): Promise<boolean> {
    const numberOfRosters = await Firestore.getCount(
      undeletedQuery(
        Roster.col(staffer),
        where('item.event.type', '==', EventType.RosteredOn),
        where('item.event.practice.ref', '==', practice.ref)
      )
    );

    return numberOfRosters > 0;
  }

  static hasActiveRosterAfterDate(
    schedules: ICalendarEventSchedule[],
    date: moment.Moment,
    timezone: Timezone
  ): boolean {
    const cutoff = toMomentTz(date, timezone);

    return schedules.some((schedule) => {
      const rosterEnd = endsOnDate(schedule.pattern)
        ? toMomentTz(schedule.pattern.endingDate, timezone)
        : undefined;

      return !rosterEnd || rosterEnd.isSameOrAfter(cutoff, 'day');
    });
  }

  static getByDay(schedule: IRoster, dayOfWeek: DayOfWeek): IRosterTime[] {
    return schedule.days.filter(
      (scheduleDay: IRosterTime) => scheduleDay.dayOfWeek === dayOfWeek
    );
  }

  static getOpeningTimes(
    schedule: IRoster,
    dayOfWeek: DayOfWeek
  ): IScheduleRange | undefined {
    const days: IRosterTime[] = this.getByDay(schedule, dayOfWeek);
    return days.length ? days[0].shift : undefined;
  }

  static getScheduleTimes(
    schedules: ICalendarEventSchedule[],
    range: ITimePeriod,
    timezone: Timezone
  ): ITimePeriod[] {
    return reduceToSingleArray(
      schedules.map((schedule) =>
        Schedule.forecast(schedule, range, timezone).map((date) =>
          scheduleTimeToTimePeriod(
            schedule.scheduleTime,
            toMomentTz(date, timezone)
          )
        )
      )
    );
  }

  static isRosteredOn(schedules: ITimePeriod[], day: moment.Moment): boolean {
    if (!schedules.length) {
      return false;
    }

    return schedules.some((schedule) => schedule.from.isSame(day, 'day'));
  }

  static rosteredTimes$(
    staffer: WithRef<IStaffer>,
    range: ITimePeriod,
    brand: IReffable<IBrand>,
    practice: WithRef<IPractice>,
    rosterSchedules?: WithRef<ICalendarEventSchedule>[],
    includeRosterHistory: boolean = false
  ): Observable<ITimePeriod[]> {
    return from(TimezoneResolver.fromPracticeRef(practice.ref)).pipe(
      switchMap((timezone) => {
        const timezoneAwareRange = {
          from: toMomentTz(range.from, timezone),
          to: toMomentTz(range.to, timezone),
        };

        return Roster.getRosterEvents$(
          staffer,
          timezoneAwareRange,
          brand,
          practice,
          rosterSchedules,
          includeRosterHistory
        ).pipe(
          map((events) =>
            events.map((event) => ({
              from: toMomentTz(event.event.from, timezone),
              to: toMomentTz(event.event.to, timezone),
            }))
          )
        );
      })
    );
  }

  static getRosterEvents$(
    staffer: WithRef<IStaffer>,
    range: ITimePeriod,
    brand: IReffable<IBrand>,
    practice: WithRef<IPractice>,
    rosterSchedules?: WithRef<ICalendarEventSchedule>[],
    includeRosterHistory = false
  ): Observable<(WithRef<ICalendarEvent> | ICalendarEvent)[]> {
    const rosteredOnEvents$ = Brand.eventQueryV2$(
      brand,
      range,
      'event.from',
      [staffer.ref],
      EventType.RosteredOn,
      practice.ref
    );

    const rosterSchedules$ = rosterSchedules
      ? of(rosterSchedules)
      : Roster.all$(
          staffer,
          [EventType.RosteredOn],
          practice,
          includeRosterHistory
        );

    const calendarEvents$ = rosterSchedules$.pipe(
      multiSwitchMap((schedule) =>
        CalendarEventSchedule.buildEvents(schedule, range)
      ),
      map(reduceToSingleArray)
    );

    return combineLatest([rosteredOnEvents$, calendarEvents$]).pipe(
      map(([rosteredOnEvents, calendarEvents]) => [
        ...rosteredOnEvents,
        ...calendarEvents,
      ])
    );
  }
}
